<template>
  <ResponsiveLayout
    sticky-actions
    :progress="0.5"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="back" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_quiz_cat_nav_title') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <h3
        class="kds-typography-title-xlarge"
        v-html="$t('b2b_quiz_cat_questions_description')"
      />
      <div
        v-for="catQuestion in catQuestions"
        :key="catQuestion.key"
        class="form-container"
      >
        <div class="table">
          <div class="table__left">
            <div v-html="$t(catQuestion.min_description)" />
          </div>
          <div class="table__center">
            <CATListItem
              :question-key="catQuestion.key"
              :state="!errors[catQuestion.key]"
              :update="updateQuestion"
              :value="catQuestion.value"
            />
          </div>
          <div class="table__right">
            <div v-html="$t(catQuestion.max_description)" />
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <BaseButton
        :loading="isLoading"
        data-qa="submit"
        @click="next"
      >
        {{ $t('b2b_quiz_cat_next') }}
      </BaseButton>
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import useCATStore from '@shared/modules/assessment/stores/useCATStore.js'
import ROUTE from '@shared/modules/assessment/router/names'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import CATListItem from '@shared/components/Quiz/CATListItem.vue'
import { useI18n } from 'petite-vue-i18n'
import * as apiRequest from '@shared/api-endpoints'

const router = useRouter()
const catStore = useCATStore()
const { t } = useI18n()

const errors = ref({})
const isLoading = ref(false)

const catQuestions = computed(() => catStore.catQuestions)
const catAnswers = computed(() => catStore.catAnswers)
const catQuestionsScore = computed(() => catStore.catQuestionsScore)
const catSummary = computed(() => catStore.catSummary)

const updateQuestion = ({ name, value }) => {
  catStore.answers[name] = value
  catStore.updateCATScore()
  delete errors.value[name]
}

const validate = () => {
  const errors = {}

  catQuestions.value.forEach((catQuestion) => {
    if (catQuestion.value === null) {
      errors[catQuestion.key] = t('generic_field_required_unlabeled')
    }
  })

  return {
    isValid: !Object.keys(errors).length,
    errors,
  }
}

const back = () => {
  router.push({ name: ROUTE.ASSESSMENT_CAT_WELCOME })
}

const formIsDisabled = ref(false)

const next = async () => {
  if (formIsDisabled.value) {
    return
  }

  const { errors: validationErrors, isValid } = validate()
  errors.value = validationErrors

  if (!isValid) {
    return
  }
  formIsDisabled.value = true

  try {
    isLoading.value = true
    await apiRequest.submitCAT({
      answers: catAnswers.value,
      score: catQuestionsScore.value,
      group: catSummary.value.summary.group,
    })
  } catch (error) {
    isLoading.value = false
    console.error(error)
  } finally {
    await router.push({
      name: ROUTE.ASSESSMENT_CAT_COMPLETED,
    })
  }
}
</script>

<style lang="scss" scoped>
.form-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-bottom: 1px solid $kds-color-line-subtle;
  padding-bottom: 24px;
  line-height: 145%;
}

.form-container:last-child {
  border-bottom: 0;
}

.table {
  display: grid;
  grid-gap: 0 20px;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'left right'
    'center center';

  &__left {
    grid-area: left;
    @include kds-typography-paragraph-medium;
    word-wrap: break-word;
    overflow: hidden;
  }

  &__center {
    grid-area: center;
    display: flex;
  }

  &__right {
    @include kds-typography-paragraph-medium;
    grid-area: right;
    text-align: right;
    word-wrap: break-word;
    overflow: hidden;
  }
}
</style>
